import { useEffect, useState } from "react";
import { Scale, Moon, Sparkles, ChevronDown, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/format";

const WHATSAPP = "918088785201";
const enquireLink = (name) =>
  `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi WhatTheBark! I'd like to enquire about ${name} for my pup.`)}`;

const PolicyToggle = ({ service }) => {
  const [open, setOpen] = useState(false);
  if (!service.policy) return null;
  return (
    <div className="mt-4">
      <button
        type="button"
        data-testid={`see-policy-${service.slug}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3.5 py-1.5 text-[11px] font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
      >
        See policy
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <p data-testid={`policy-text-${service.slug}`} className="mt-3 rounded-2xl bg-cream-dark/50 p-4 text-xs leading-relaxed text-slate-600">
          {service.policy}
        </p>
      )}
    </div>
  );
};

const PriceRows = ({ rows }) => (
  <ul className="mt-4 divide-y divide-ink/6">
    {rows.map((r) => (
      <li key={r.label} className="flex items-center justify-between gap-3 py-2.5 text-sm">
        <span className="font-medium text-navy">{r.label}</span>
        <span className="font-display font-medium text-ink">{r.value}</span>
      </li>
    ))}
  </ul>
);

const ServiceCard = ({ svc, addons }) => {
  const mode = svc.pricing_mode;
  const svcAddons = addons.filter((a) => (a.service_slug || "") === svc.slug);
  let body = null;
  if (mode === "weight") {
    body = <PriceRows rows={(svc.tiers || []).map((t) => ({ label: t.label, value: `${fmtINR(t.price)} / ${svc.unit}` }))} />;
  } else if (mode === "weight_option") {
    body = (
      <ul className="mt-4 divide-y divide-ink/6">
        {(svc.tiers || []).map((t) => (
          <li key={t.label} className="flex items-center justify-between gap-3 py-2.5 text-sm">
            <span className="font-medium text-navy">{t.label}</span>
            <span className="font-display font-medium text-ink">
              {fmtINR(t.half)} <span className="text-xs font-normal text-slate-400">half</span> · {fmtINR(t.full)}{" "}
              <span className="text-xs font-normal text-slate-400">full</span>
            </span>
          </li>
        ))}
      </ul>
    );
  } else if (mode === "enquire" && (svc.options || []).some((o) => o.price)) {
    body = <PriceRows rows={(svc.options || []).map((o) => ({ label: o.label, value: fmtINR(o.price) }))} />;
  } else if (mode === "request") {
    body = (
      <ul className="mt-4 space-y-2 text-sm">
        {(svc.options || []).map((o) => (
          <li key={o.key} className="flex items-center justify-between gap-3">
            <span className="font-medium text-navy">{o.label}</span>
            <span className="text-xs font-semibold text-bark">On request</span>
          </li>
        ))}
      </ul>
    );
  } else {
    body = <p className="mt-4 text-sm text-slate-500">Enquire for the current calendar, pricing and slots.</p>;
  }

  return (
    <div data-testid={`rates-service-${svc.slug}`} className="rounded-3xl border border-ink/8 bg-white p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl font-medium text-ink">{svc.name}</p>
          {svc.description && <p className="mt-0.5 text-xs text-slate-500">{svc.description}</p>}
        </div>
        {(mode === "enquire" || mode === "request") && (
          <a
            href={enquireLink(svc.name)}
            target="_blank"
            rel="noreferrer"
            data-testid={`rates-enquire-${svc.slug}`}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-1.5 text-[11px] font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Enquire
          </a>
        )}
      </div>
      {body}
      {svcAddons.length > 0 && (
        <div className="mt-4 rounded-2xl bg-cream-dark/40 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Add-ons (per pup)</p>
          <ul className="mt-2 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {svcAddons.map((a) => (
              <li key={a.addon_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-600">{a.name}</span>
                <span className="font-semibold text-ink">{fmtINR(a.price)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <PolicyToggle service={svc} />
    </div>
  );
};

export const RatesSection = () => {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/addons")])
      .then(([s, a]) => {
        setServices(s.data);
        setAddons(a.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div id="rates" data-testid="rates-section" className="mt-10 rounded-[2rem] border border-ink/8 bg-cream-dark/30 p-6 sm:p-8">
      <p className="text-sm text-slate-500">
        Priced per pup by weight. Your exact total — with kennel sharing, add-ons and any access fees — is always shown before you pay.
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="flex items-start gap-2.5 rounded-2xl bg-white p-4">
          <Scale className="mt-0.5 h-4 w-4 shrink-0 text-bark" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-slate-600"><span className="font-semibold text-ink">Per pup, by weight.</span> Each pup pays their own tier.</p>
        </div>
        <div className="flex items-start gap-2.5 rounded-2xl bg-white p-4">
          <Moon className="mt-0.5 h-4 w-4 shrink-0 text-bark" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-slate-600"><span className="font-semibold text-ink">Share a kennel, save 40%.</span> Night boarding — 40% off the 2nd &amp; 3rd pup.</p>
        </div>
        <div className="flex items-start gap-2.5 rounded-2xl bg-white p-4">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-bark" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-slate-600"><span className="font-semibold text-ink">Access fee ₹200.</span> Early (6–8 AM) or late (6–9 PM) check-in/out.</p>
        </div>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-slate-400">Loading rates…</p>
      ) : (
        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          {services.map((svc, i) => (
            <Reveal key={svc.service_id} delay={0.03 * (i % 2)}>
              <ServiceCard svc={svc} addons={addons} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};
